import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Action, UserType } from '../common.js';
import { AddPrivacySetting } from '../api/finn-privacy-settings-api.js';
import { trackUpdateSchibstedConsent } from './schibsted-privacy-settings-tracking.js';
import { I18n } from '@lingui/core';
import { Scope } from '../scope.js';

export type SchibstedPrivacySettingsContextType = {
    consentUser: ConsentUser;
    environmentId?: string;
    privacySettings?: PrivacySettings;
    brandPrivacySettings?: PrivacySettings;
    fetchPrivacySettings: () => void;
    fetchBrandPrivacySettings: () => void;
    withdrawObjection: (
        setting: ObjectionSetting,
        scope: Scope,
    ) => Promise<void>;
    addObjection: (setting: ObjectionSetting, scope: Scope) => Promise<void>;
    addConsent: (setting: ConsentSetting, scope: Scope) => Promise<void>;
    withdrawConsent: (setting: ConsentSetting, scope: Scope) => Promise<void>;
    brand: string;
};

export const defaultContext: SchibstedPrivacySettingsContextType = {
    consentUser: {
        userId: '0',
        userType: UserType.NOT_LOGGED_IN,
        isLoggedIn: false,
    },
    privacySettings: { consentCategories: [], objectionCategories: [] },
    fetchPrivacySettings: () => {},
    fetchBrandPrivacySettings: () => {},
    withdrawObjection: () => Promise.resolve(),
    addObjection: () => Promise.resolve(),
    withdrawConsent: () => Promise.resolve(),
    addConsent: () => Promise.resolve(),
    brand: 'FINN',
};

export const SchibstedPrivacySettingsContext =
    React.createContext<SchibstedPrivacySettingsContextType>(defaultContext);
export type SchibstedPrivacySettingsProps = {
    baseUrl: string;
    environmentId?: string;
    consentUser: ConsentUser;
    children: React.ReactNode;
    hmacToken: string;
    privacySettings?: PrivacySettings;
    brandPrivacySettings?: PrivacySettings;
    i18n: I18n;
    brand: string;
};

export function SchibstedPrivacySettingsContextProvider(
    props: SchibstedPrivacySettingsProps,
): React.ReactElement {
    const [privacySettings, setPrivacySettings] = useState(
        props.privacySettings,
    );
    const [brandPrivacySettings, setBrandPrivacySettings] = useState(
        props.brandPrivacySettings,
    );
    const handleError = (error: Error, scope?: Scope) => {
        console.error(error);
        if (scope === Scope.GLOBAL) {
            fetchPrivacySettings();
        }
        if (scope === Scope.BRAND) {
            fetchBrandPrivacySettings();
        }
        toast.error(
            props.i18n.t({
                id: 'privacy-settings.toast.error',
                comment:
                    'Error message shown to the user when something goes wrong',
            }),
        );
    };

    function fetchPrivacySettings() {
        const url = props.consentUser.isLoggedIn
            ? '/api/privacy-settings'
            : `/api/anonymous/${props.environmentId}/privacy-settings`;

        fetch(`${props.baseUrl}${url}`, {
            credentials: 'same-origin',
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'x-csrf-token': props.hmacToken,
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `Bad response when fetching privacy-settings: ${response.statusText}`,
                    );
                }
                return response.json();
            })
            .then(setPrivacySettings)
            .catch((error) => handleError(error));
    }
    function fetchBrandPrivacySettings() {
        const url = '/api/brand-privacy-settings';

        fetch(`${props.baseUrl}${url}`, {
            credentials: 'same-origin',
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'x-csrf-token': props.hmacToken,
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `Bad response when fetching brand privacy-settings: ${response.statusText}`,
                    );
                }
                return response.json();
            })
            .then(setBrandPrivacySettings)
            .catch((error) => handleError(error));
    }

    function withdrawObjection(
        setting: ObjectionSetting,
        scope: Scope,
    ): Promise<void> {
        const url = props.consentUser.isLoggedIn
            ? `/api/objection/${setting.currentState.objectionKey?.id}/${scope}`
            : `/api/anonymous/${props.environmentId}/objection/${setting.currentState.objectionKey?.id}`;

        return fetch(`${props.baseUrl}${url}`, {
            credentials: 'same-origin',
            method: 'DELETE',
            headers: {
                Accept: 'application/json',
                'x-csrf-token': props.hmacToken,
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `Bad response when withdrawing objection: ${response.statusText}`,
                    );
                }
            })
            .then(() => {
                setting.currentState.hasObjection = false;
                setting.currentState.objectionKey = undefined;
            })
            .then(() => {
                toast.success(
                    props.i18n.t({
                        id: 'privacy-settings.toast.success',
                        comment:
                            'Success message shown to the user when the consent is updated',
                    }),
                );
            })
            .then(() =>
                trackUpdateSchibstedConsent(
                    setting.activities[0],
                    Action.ACCEPTED,
                ),
            )
            .catch((error) => handleError(error, scope));
    }

    function addObjection(
        setting: ObjectionSetting,
        scope: Scope,
    ): Promise<void> {
        const url = props.consentUser.isLoggedIn
            ? '/api/objection'
            : `/api/anonymous/${props.environmentId}/objection`;

        return fetch(`${props.baseUrl}${url}`, {
            credentials: 'same-origin',
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'x-csrf-token': props.hmacToken,
            },
            body: JSON.stringify({
                key: setting.key,
                activities: setting.activities,
                scope,
            } as AddPrivacySetting),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `Bad response when adding objection: ${response.statusText}`,
                    );
                }
                return response.json();
            })
            .then((key: Key) => {
                setting.currentState.hasObjection = true;
                setting.currentState.objectionKey = key;
            })
            .then(() => {
                toast.success(
                    props.i18n.t({
                        id: 'privacy-settings.toast.success',
                        comment:
                            'Success message shown to the user when the consent is updated',
                    }),
                );
            })
            .then(() =>
                trackUpdateSchibstedConsent(
                    setting.activities[0],
                    Action.WITHDRAWN,
                ),
            )
            .catch((error) => handleError(error, scope));
    }

    function addConsent(setting: ConsentSetting, scope: Scope): Promise<void> {
        return fetch(`${props.baseUrl}/api/consent`, {
            credentials: 'same-origin',
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'x-csrf-token': props.hmacToken,
            },
            body: JSON.stringify({
                key: setting.key,
                activities: setting.activities,
                scope,
            } as AddPrivacySetting),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `Bad response when adding objection: ${response.statusText}`,
                    );
                }
                return response.json();
            })
            .then((key: Key) => {
                setting.currentState.hasConsent = true;
                setting.currentState.consentKey = key;
            })
            .then(() => {
                toast.success(
                    props.i18n.t({
                        id: 'privacy-settings.toast.success',
                        comment:
                            'Success message shown to the user when the consent is updated',
                    }),
                );
            })
            .then(() =>
                trackUpdateSchibstedConsent(
                    setting.activities[0],
                    Action.ACCEPTED,
                ),
            )
            .catch((error) => handleError(error, scope));
    }

    function withdrawConsent(
        setting: ConsentSetting,
        scope: Scope,
    ): Promise<void> {
        return fetch(
            `${props.baseUrl}/api/consent/${setting.currentState.consentKey?.id}/${scope}`,
            {
                credentials: 'same-origin',
                method: 'DELETE',
                headers: {
                    Accept: 'application/json',
                    'x-csrf-token': props.hmacToken,
                },
            },
        )
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `Bad response when withdrawing consent: ${response.statusText}`,
                    );
                }
            })
            .then(() => {
                setting.currentState.hasConsent = false;
                setting.currentState.consentKey = undefined;
            })
            .then(() => {
                toast.success(
                    props.i18n.t({
                        id: 'privacy-settings.toast.success',
                        comment:
                            'Success message shown to the user when the consent is updated',
                    }),
                );
            })
            .then(() =>
                trackUpdateSchibstedConsent(
                    setting.activities[0],
                    Action.WITHDRAWN,
                ),
            )
            .catch((error) => handleError(error, scope));
    }

    return (
        <SchibstedPrivacySettingsContext.Provider
            value={{
                consentUser: props.consentUser,
                environmentId: props.environmentId,
                brand: props.brand,
                privacySettings,
                brandPrivacySettings,
                fetchBrandPrivacySettings,
                fetchPrivacySettings,
                withdrawObjection,
                addObjection,
                addConsent,
                withdrawConsent,
            }}
        >
            {props.children}
        </SchibstedPrivacySettingsContext.Provider>
    );
}
